/**
 * Input 스타일
 * @description 색 축은 disabled → error → 나머지 순으로 정해진다. 나머지(rest·focused·filled)는
 *   CSS 가 가른다. 테두리는 inset box-shadow 다 — 포커스에서 굵기가 1 → 1.5 로 바뀌어도
 *   상자 크기가 변하면 안 된다.
 */
import { cva } from "class-variance-authority";

type ColorAxis = { disabled?: boolean | null; error?: boolean | null };

const byAxis = (rest: string, error: string, disabled: string) =>
  cva("", {
    variants: {
      disabled: { true: disabled, false: "" },
      error: { true: error, false: rest },
    },
    compoundVariants: [{ disabled: true, error: true, class: disabled }],
    defaultVariants: { disabled: false, error: false },
  });

export const inputStyles = {
  root: "flex flex-col",
  labelRow:
    "mb-[var(--fw-input-gap-label)] flex items-baseline gap-[var(--fw-space-gap-xs)]",
  label: byAxis(
    "fw-l2 text-[var(--fw-input-color-content-label-rest)]",
    "fw-l2 text-[var(--fw-input-color-content-label-error)]",
    "fw-l2 text-[var(--fw-input-color-content-label-disabled)]",
  ),
  helper: byAxis(
    "fw-caption mt-[var(--fw-input-gap-helper)] text-[var(--fw-input-color-content-helper-rest)]",
    "fw-caption mt-[var(--fw-input-gap-helper)] text-[var(--fw-input-color-content-helper-error)]",
    "fw-caption mt-[var(--fw-input-gap-helper)] text-[var(--fw-input-color-content-helper-disabled)]",
  ),
  box: cva(
    [
      "flex gap-[var(--fw-input-gap-field)]",
      "rounded-[var(--fw-input-radius)]",
      "px-[var(--fw-input-padding-field-horizontal)]",
      "py-[var(--fw-input-padding-field-vertical)]",
      "bg-[var(--fw-input-color-surface-rest)]",
      "[&_svg]:size-[var(--fw-input-icon)]",
      "[&_svg]:text-[var(--fw-input-color-content-field-rest-icon)]",
    ],
    {
      variants: {
        multiline: { true: "items-start", false: "items-center" },
        disabled: {
          true: "shadow-[inset_0_0_0_var(--fw-input-stroke-width-rest)_var(--fw-input-color-stroke-disabled)]",
          false: "",
        },
        error: {
          true: "shadow-[inset_0_0_0_var(--fw-input-stroke-width-pressed)_var(--fw-input-color-stroke-error)]",
          false: [
            "shadow-[inset_0_0_0_var(--fw-input-stroke-width-rest)_var(--fw-input-color-stroke-rest)]",
            "focus-within:shadow-[inset_0_0_0_var(--fw-input-stroke-width-pressed)_var(--fw-input-color-stroke-focused)]",
          ],
        },
      },
      compoundVariants: [
        {
          disabled: true,
          error: true,
          class:
            "shadow-[inset_0_0_0_var(--fw-input-stroke-width-rest)_var(--fw-input-color-stroke-disabled)]",
        },
      ],
      defaultVariants: { multiline: false, disabled: false, error: false },
    },
  ),
  field: cva(
    [
      "fw-b1 min-w-0 flex-1 bg-transparent outline-none",
      // 값이 없을 때(=placeholder 가 보일 때)와 있을 때 글자색이 다르다.
      "text-[var(--fw-input-color-content-field-filled)]",
      "placeholder:text-[var(--fw-input-color-content-field-rest)]",
      "disabled:cursor-not-allowed",
      "disabled:text-[var(--fw-input-color-content-field-disabled)]",
    ],
    {
      variants: {
        error: {
          true: "text-[var(--fw-input-color-content-field-error)]",
          false: "",
        },
        // 높이를 줄 수로 못박는다 — 입력에 따라 상자가 자라면 아래 내용이 밀린다.
        multiline: { true: "resize-none", false: "" },
      },
      defaultVariants: { error: false, multiline: false },
    },
  ),
  followingButton: "shrink-0 cursor-pointer disabled:cursor-not-allowed",
  followingDecoration: "shrink-0",
};

export type { ColorAxis as InputColorAxis };
